import { useRef, type ReactNode } from 'react'
import { useFrame } from '@react-three/fiber'
import { Matrix4, Quaternion, Vector3, type Group } from 'three'

/** Pose de mundo decomposta, para interpolar posição, giro e escala em separado. */
interface PoseMundo {
  posicao: Vector3
  giro: Quaternion
  escala: Vector3
}

const novaPose = (): PoseMundo => ({ posicao: new Vector3(), giro: new Quaternion(), escala: new Vector3(1, 1, 1) })
const suavizar = (t: number) => 0.5 - Math.cos(t * Math.PI) / 2

/**
 * Faz o conteúdo deslizar quando a pose de destino salta.
 *
 * O grupo fica onde a cadeia de transformações dos pais o colocaria. Quando
 * `chave` muda (a ferramenta passou para outra zona), ele parte da pose que
 * estava mostrando e chega à nova em `duracaoS`, acompanhando o destino vivo,
 * em vez de saltar. Fora dessas transições, segue a cadeia sem atraso, para
 * não descolar a ponta da ferramenta do marcador durante o trabalho.
 *
 * Só desliza enquanto permanece montado: ao aparecer, já nasce no destino.
 */
export function TransicaoSuave({
  chave,
  ativa = true,
  duracaoS = 0.6,
  children,
}: {
  chave: string | null | undefined
  ativa?: boolean
  duracaoS?: number
  children: ReactNode
}) {
  const grupo = useRef<Group>(null)
  const estado = useRef({
    chave,
    mostrada: null as PoseMundo | null,
    inicio: null as PoseMundo | null,
    t: 0,
    destino: novaPose(),
    atual: novaPose(),
    mundo: new Matrix4(),
    inversa: new Matrix4(),
  })

  useFrame((_, delta) => {
    const g = grupo.current
    const pai = g?.parent
    if (!g || !pai) return
    const e = estado.current
    pai.updateWorldMatrix(true, false)
    pai.matrixWorld.decompose(e.destino.posicao, e.destino.giro, e.destino.escala)

    if (e.chave !== chave) {
      e.chave = chave
      if (ativa && e.mostrada) {
        e.inicio = novaPose()
        e.inicio.posicao.copy(e.mostrada.posicao)
        e.inicio.giro.copy(e.mostrada.giro)
        e.inicio.escala.copy(e.mostrada.escala)
        e.t = 0
      }
    }

    if (e.inicio) {
      e.t = Math.min(1, e.t + delta / duracaoS)
      const k = suavizar(e.t)
      e.atual.posicao.lerpVectors(e.inicio.posicao, e.destino.posicao, k)
      e.atual.giro.slerpQuaternions(e.inicio.giro, e.destino.giro, k)
      e.atual.escala.lerpVectors(e.inicio.escala, e.destino.escala, k)
      if (e.t >= 1) e.inicio = null
    } else {
      e.atual.posicao.copy(e.destino.posicao)
      e.atual.giro.copy(e.destino.giro)
      e.atual.escala.copy(e.destino.escala)
    }

    e.mostrada ??= novaPose()
    e.mostrada.posicao.copy(e.atual.posicao)
    e.mostrada.giro.copy(e.atual.giro)
    e.mostrada.escala.copy(e.atual.escala)

    // local = inversa(mundo do pai) × mundo desejado
    e.mundo.compose(e.atual.posicao, e.atual.giro, e.atual.escala)
    g.matrix.copy(e.inversa.copy(pai.matrixWorld).invert().multiply(e.mundo))
    g.matrixWorldNeedsUpdate = true
  })

  return (
    <group ref={grupo} matrixAutoUpdate={false}>
      {children}
    </group>
  )
}
